import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ScoreAxis } from "@resume-cloudflare/shared";
import { api } from "./api";

// Mirrors db/insights.ts in the worker. Source of truth lives there.
export interface FunnelCounts {
  interested: number;
  preparing: number;
  approved: number;
  applied: number;
  interviewing: number;
  offer: number;
  rejected: number;
  withdrawn: number;
  total: number;
}

export interface RateBucket {
  label: string;
  key: string;
  submitted: number;
  responded: number;
  rate: number;
}

export interface PerAxisCut {
  axis: ScoreAxis;
  buckets: RateBucket[];
}

export interface UnderperformingCut {
  dimension: string;
  label: string;
  key: string;
  submitted: number;
  responded: number;
  rate: number;
  delta: number;
}

export interface CadenceWeek {
  week_start: number;
  submissions: number;
  warm_touches: number;
}

export interface WaitingStat {
  count: number;
  median_days: number | null;
  max_days: number | null;
}

// Mirrors lib/cadence.ts (SUBMISSION_FLOW_SPRINTS PR E).
export interface Cadence {
  target_per_week: number;
  floor_per_week: number;
  weeks: CadenceWeek[];
  this_week: { submissions: number; warm_touches: number };
  floor_streak_weeks: number;
  waiting: { preparing: WaitingStat; approved: WaitingStat };
  approval_to_submit: { n: number; median_hours: number | null };
}

export interface InsightsAggregate {
  generated_at: number;
  cached?: boolean;
  funnel: FunnelCounts;
  overall: {
    submitted: number;
    responded: number;
    rate: number;
  };
  // Session 7 PR2 — stuck = applied >14d with no interview event;
  // progressed = matches the centroid's source predicate (interviewing,
  // offer, or any interview_added event). Both default to undefined when
  // talking to a pre-PR2 server (forward-compatible) — current SPAs treat
  // missing as 0.
  stuck_count?: number;
  progressed_count?: number;
  by_score_band: RateBucket[];
  by_ats: RateBucket[];
  by_tier: RateBucket[];
  by_axis: PerAxisCut[];
  // Cuts lagging the overall rate enough, on enough volume, to act on.
  // Undefined when talking to a pre-PR4 server.
  underperforming?: UnderperformingCut[];
  // Undefined on a pre-PR-E cached payload.
  cadence?: Cadence;
}

export function useInsights() {
  return useQuery({
    queryKey: ["insights"],
    queryFn: () => api<InsightsAggregate>("/api/insights"),
    // The cache is server-side (KV, 5 min); the client just rerenders on
    // explicit refresh. Don't poll — funnel changes only when the user
    // moves an application.
    staleTime: 60_000,
  });
}

export function useRefreshInsights() {
  const qc = useQueryClient();
  return async () => {
    const fresh = await api<InsightsAggregate>("/api/insights?refresh=1");
    qc.setQueryData(["insights"], fresh);
    return fresh;
  };
}
